import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, X, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const TwoFactorSettings = () => {
  const { t } = useLanguage();
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    checkTwoFactorStatus();
  }, []);

  const checkTwoFactorStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/2fa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFactorEnabled(response.data.enabled);
    } catch (err) {
      console.error('Error checking 2FA status:', err);
    }
  };

  const handleSetup2FA = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/2fa/setup`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQrCode(response.data.qrCode);
      setSecret(response.data.secret);
      setShowSetup(true);
    } catch (err) {
      setError(err.response?.data?.error || t('setupError'));
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/2fa/enable`,
        { token: verificationCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(t('enabledSuccess'));
      setTwoFactorEnabled(true);
      setTimeout(() => {
        setShowSetup(false);
        setVerificationCode('');
        setQrCode('');
        setSecret('');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || t('invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/2fa/disable`,
        { password },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(t('disabledSuccess'));
      setTwoFactorEnabled(false);
      setPassword('');
    } catch (err) {
      setError(err.response?.data?.error || t('disableError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modern-section">
      <div className="admin-container">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="text-gold" size={32} />
          <div>
            <h2 className="section-title">
              {t('twoFactorAuth')}
            </h2>
            <p className="section-subtitle">
              {t('protectAccountDescription')}
            </p>
          </div>
        </div>

        {error && (
          <div className="alert alert-error mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success mb-4 flex items-center gap-2">
            <Check size={20} />
            {success}
          </div>
        )}

        {/* Status */}
        <div className="admin-widget mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="widget-title">
                {t('status2FA')}
              </p>
              <p className="widget-subtitle">
                {twoFactorEnabled ? t('enabled') : t('disabled')}
              </p>
            </div>
            <div className={`px-4 py-2 rounded-full font-bold ${twoFactorEnabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
              {twoFactorEnabled ? '✓ ' + t('active') : '✗ ' + t('inactive')}
            </div>
          </div>
        </div>

        {/* Enable 2FA */}
        {!twoFactorEnabled && !showSetup && (
          <div>
            <button
              onClick={handleSetup2FA}
              disabled={loading}
              className="modern-button-primary w-full"
            >
              {loading ? t('loading') : t('enable2FA')}
            </button>
          </div>
        )}

        {/* Setup 2FA */}
        {showSetup && !twoFactorEnabled && (
          <div>
            <div className="admin-widget mb-6">
              <h3 className="widget-title mb-4">
                {t('step1ScanQR')}
              </h3>
              <p className="widget-subtitle mb-4">
                {t('useAuthenticatorApp')}
              </p>
              {qrCode && (
                <div className="flex justify-center mb-4">
                  <img src={qrCode} alt="QR Code" className="w-64 h-64 border-2 border-gold/30 rounded-lg p-4" />
                </div>
              )}
              <div className="bg-gray-900/50 border-2 border-gold/20 p-4 rounded-lg">
                <p className="text-xs text-gray-400 mb-2">
                  {t('orEnterManually')}
                </p>
                <p className="font-mono text-sm text-gold font-bold break-all">
                  {secret}
                </p>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="widget-title mb-3">
                {t('step2EnterCode')}
              </h3>
              <input
                type="text"
                placeholder={t('codePlaceholder')}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="modern-form-input text-center text-2xl font-mono tracking-widest"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSetup(false);
                  setQrCode('');
                  setSecret('');
                  setVerificationCode('');
                }}
                className="modern-button-secondary flex-1"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleEnable2FA}
                disabled={loading || verificationCode.length !== 6}
                className="modern-button-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('checking') : t('confirmAction')}
              </button>
            </div>
          </div>
        )}

        {/* Disable 2FA */}
        {twoFactorEnabled && (
          <div>
            <div className="alert alert-warning mb-6">
              {t('disableWarning')}
            </div>
            <div className="form-group mb-6">
              <label className="form-label">
                {t('enterPasswordToConfirm')}
              </label>
              <input
                type="password"
                placeholder={t('yourPassword')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="modern-form-input"
              />
            </div>
            <button
              onClick={handleDisable2FA}
              disabled={loading || !password}
              className="modern-button-danger w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('loading') : t('disable2FA')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorSettings;
